import type { InsightMeta } from '../visualization.types'
import type { VariableUnitFormat } from '~/packages/variable/variables.typing'
import { formatAsBrNumber, formatAsMoney } from '~/prix'

export const stripParens = (s?: string | null) =>
  (s ?? '').replace(/\s*\(.*?\)\s*$/, '').trim() || null

export const formatBR = (v: number, opts?: Intl.NumberFormatOptions) => {
  try {
    return formatAsBrNumber(v)
  } catch {}
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2, ...opts }).format(v)
}

export const formatBRShort = (v: number) => {
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs % 1_000_000 ? 1 : 0)}M`
  if (abs >= 1_000) return `${(v / 1_000).toFixed(abs % 1_000 ? 1 : 0)}k`
  return formatBR(v)
}

export const formatPercent = (value: number | null | undefined) =>
  value == null || !isFinite(value) ? '—' : `${(value * 100).toFixed(2).replace('.', ',')}%`

export const formatVisualizationValue = (
  value: number | null | undefined,
  unitFormat?: VariableUnitFormat | string | null,
) => {
  if (value == null || !Number.isFinite(value)) {
    return '—'
  }

  switch (unitFormat) {
    case 'percentage': {
      const roundedValue = Number(value.toFixed(2))
      const displayValue = roundedValue.toLocaleString('pt-BR', {
        minimumFractionDigits: Number.isInteger(roundedValue) ? 0 : 2,
        maximumFractionDigits: 2,
      })

      return `${displayValue}%`
    }
    case 'money':
      return formatAsMoney(value, 'BRL')
    default:
      return formatAsBrNumber(value)
  }
}

export const resolveVisualizationUnitFormat = (
  meta?: InsightMeta | null,
): VariableUnitFormat | string | undefined => {
  if (meta?.unitFormat) {
    return meta.unitFormat
  }

  const hasPercentageDenominator =
    typeof meta?.reference?.denominator === 'number' ||
    typeof meta?.comparison?.denominator === 'number'

  return hasPercentageDenominator ? 'percentage' : undefined
}
